import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useMemo, useState } from "react";

import {
  getPmlResumen,
  type Mercado,
  type NodoTipo,
  type PmlResumenRow,
} from "@/lib/api";

type SelectedNode = { tipo: NodoTipo; clave: string };

type Props = {
  open: boolean;
  onClose: () => void;
  mercado: Mercado;
  nodos: SelectedNode[];
  desde?: string;
  hasta?: string;
};

type Agrupado = "anual" | "mensual" | "horario";

const TABS: { key: Agrupado; label: string }[] = [
  { key: "anual", label: "Anual" },
  { key: "mensual", label: "Mensual" },
  { key: "horario", label: "Horario" },
];

const MESES = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

const PRECIO_FMT = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmt(value: string | null | undefined): string {
  if (value == null) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return PRECIO_FMT.format(n);
}

function nodeKey(n: { tipo: string; clave: string }): string {
  return `${n.tipo}:${n.clave}`;
}

/** Etiqueta del periodo según el agrupado activo. */
function periodo(row: PmlResumenRow, agrupado: Agrupado): { key: string; label: string } {
  if (agrupado === "anual") {
    return { key: String(row.anio), label: String(row.anio) };
  }
  if (agrupado === "mensual") {
    const mes = row.mes ?? 0;
    return {
      key: `${row.anio}-${String(mes).padStart(2, "0")}`,
      label: `${MESES[mes - 1] ?? mes} ${row.anio}`,
    };
  }
  return { key: String(row.hora).padStart(2, "0"), label: `${row.hora}` };
}

export function PmlResumenModal({ open, onClose, mercado, nodos, desde, hasta }: Props) {
  const [agrupado, setAgrupado] = useState<Agrupado>("mensual");

  const q = useQuery({
    queryKey: ["pml-resumen", mercado, nodos, desde, hasta],
    queryFn: () => getPmlResumen({ mercado, nodos, desde, hasta }),
    enabled: open && nodos.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Pivot: filas = periodos, columnas = nodos seleccionados (en su orden).
  const pivot = useMemo(() => {
    const rows = q.data?.[agrupado] ?? [];
    const byPeriodo = new Map<
      string,
      { label: string; valores: Map<string, PmlResumenRow> }
    >();
    for (const r of rows) {
      const p = periodo(r, agrupado);
      let slot = byPeriodo.get(p.key);
      if (!slot) {
        slot = { label: p.label, valores: new Map() };
        byPeriodo.set(p.key, slot);
      }
      slot.valores.set(nodeKey(r), r);
    }
    return [...byPeriodo.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);
  }, [q.data, agrupado]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Resumen de nodos seleccionados</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Promedio del precio ({mercado}) del{" "}
              <b className="text-slate-700">{desde ?? "inicio"}</b> al{" "}
              <b className="text-slate-700">{hasta ?? "fin"}</b> · {nodos.length}{" "}
              {nodos.length === 1 ? "nodo" : "nodos"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b">
          <div className="inline-flex border rounded overflow-hidden">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setAgrupado(t.key)}
                className={`px-4 py-1.5 text-sm border-l first:border-l-0 ${
                  agrupado === t.key
                    ? "bg-blue-600 text-white"
                    : "bg-white hover:bg-slate-50 text-slate-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {q.isLoading && (
            <div className="p-8 text-center text-slate-500 text-sm">Cargando…</div>
          )}
          {q.isError && (
            <div className="p-8 text-center text-red-600 text-sm">
              Error: {(q.error as Error).message}
            </div>
          )}
          {q.data && pivot.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm">
              Sin datos en el rango seleccionado.
            </div>
          )}
          {pivot.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 sticky top-0">
                <tr>
                  <th className="px-3 py-2 font-medium text-left whitespace-nowrap">
                    {agrupado === "anual" ? "Año" : agrupado === "mensual" ? "Mes" : "Hora"}
                  </th>
                  {nodos.map((n) => (
                    <th
                      key={nodeKey(n)}
                      className="px-3 py-2 font-medium text-right whitespace-nowrap"
                    >
                      <span
                        className={`text-[10px] font-medium px-1 py-0.5 rounded mr-1.5 ${
                          n.tipo === "PML"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {n.tipo}
                      </span>
                      <span className="font-mono text-xs">{n.clave}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pivot.map((row) => (
                  <tr key={row.label} className="border-t hover:bg-slate-50">
                    <td className="px-3 py-1.5 whitespace-nowrap">{row.label}</td>
                    {nodos.map((n) => {
                      const cell = row.valores.get(nodeKey(n));
                      return (
                        <td
                          key={nodeKey(n)}
                          className="px-3 py-1.5 text-right tabular-nums"
                          title={cell ? `${cell.n_obs.toLocaleString("es-MX")} horas` : undefined}
                        >
                          {fmt(cell?.precio)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-2.5 border-t text-[11px] text-slate-500">
          Promedio simple de las horas del periodo, en $/MWh. Pasa el cursor
          sobre una celda para ver cuántas horas promedia.
          {agrupado === "horario" && (
            <> Horario: promedio de cada hora del día (1–24) sobre todo el rango.</>
          )}
        </div>
      </div>
    </div>
  );
}
